import React, { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { msalInstance, loginRequest, safeMsalLoginRedirect, msalInteractionManager } from '../../auth/msalConfig';
import MicrosoftIcon from '@mui/icons-material/AccountBox';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { auth } from '../../services/firebase/config';

// URL de la fonction Cloud Firebase
const FIREBASE_FUNCTION_URL = 'https://us-central1-diotranscript.cloudfunctions.net/exchangeAzureTokenHttp';

/**
 * Bouton d'authentification avec Microsoft Azure AD
 */
const AzureAuthButton = ({ onSuccess, variant = 'contained', fullWidth = false, size = 'medium' }) => {
  const { signInWithAzure } = useAuth();
  const { addSystemNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  // Fonction pour attendre que l'authentification soit complètement terminée
  const waitForAuthentication = (maxWaitTime = 5000) => {
    return new Promise((resolve, reject) => {
      let waitTime = 0;
      const checkInterval = 100;
      
      const checkAuth = () => {
        // Vérifier si Firebase Auth est connecté et si nous avons un utilisateur
        if (auth.currentUser && auth.currentUser.uid) {
          resolve(true);
          return;
        }
        
        waitTime += checkInterval;
        if (waitTime >= maxWaitTime) {
          addSystemNotification('⚠️ Timeout d', 'error');
          resolve(false);
          return;
        }
        
        setTimeout(checkAuth, checkInterval);
      };
      
      checkAuth();
    });
  };

  const handleSignIn = async () => {
    try {
      setLoading(true);
      console.log('🔄 Redirection Azure AD (SSO sans popup)...');
      await safeMsalLoginRedirect();
      // La page sera redirigée par MSAL ; aucun code supplémentaire ne s'exécutera.
    } catch (error) {
      addSystemNotification('❌ Erreur lors de la redirection Azure:', 'error');
      setError(`Erreur lors de la redirection: ${error.message}`);
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <Button
        variant={variant}
        color="primary"
        fullWidth={fullWidth}
        size={size}
        onClick={handleSignIn}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : <MicrosoftIcon />}
        sx={{
          backgroundColor: '#0078d7',
          '&:hover': {
            backgroundColor: '#106ebe',
          },
        }}
      >
        {loading ? 'Connexion en cours...' : 'Se connecter avec Microsoft'}
      </Button>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Erreur de connexion</DialogTitle>
        <DialogContent>
          <Typography>
            {error || 'Une erreur s\'est produite lors de la tentative de connexion avec Microsoft.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Veuillez réessayer ou contacter l'administrateur si le problème persiste.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Fermer</Button>
          <Button variant="contained" onClick={handleSignIn} disabled={loading}>
            Réessayer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AzureAuthButton; 